import { createFileRoute } from '@tanstack/react-router';
import { PageShell, Section, SectionHeading } from '~/components/layout';
import { CtaBanner, FaqAccordion } from '~/components/sections';
import { Grid, Icon } from '~/components/ui';
import { cn } from '~/lib/cn';
import {
  partnershipTypes,
  partnershipsFaq,
  partnershipsHero,
  partnershipsPromote,
  partnershipsVideos,
} from '~/content/partnerships';
import { pageHeadMeta } from '~/lib/seo';

export const Route = createFileRoute('/partnerships')({
  head: () =>
    pageHeadMeta({
      title: 'nsave Partnerships for Influencers, Communities and Brands/Platforms',
      description:
        'Apply for a partnership and get paid to promote nsave. Join as an influencer, community, or brand/platform to earn through our partnership program.',
      path: '/partnerships',
    }),
  component: PartnershipsPage,
});

/** Small document/article glyph (inherits `currentColor`). Mirrors invest.tsx. */
function ArticleIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      className="size-5 shrink-0"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true">
      <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
      <path d="M14 3v5h5" />
    </svg>
  );
}

// Shared pill-button classes (matches ButtonLink's primary/secondary md sizes).
// Reused here for external anchors, which the router-based ButtonLink can't take.
const PILL =
  'inline-flex min-w-16 cursor-pointer items-center justify-center rounded-pill font-medium leading-[1.4] transition-colors pt-[12px] pr-[14px] pb-[13px] pl-[14px] text-[16px] tracking-[0.192px]';

/**
 * Partnerships hero — a static, centred hero (title + CTAs), matching
 * the /compare and /invest hero scaffolding rather than the animated shared
 * `Hero`. The primary CTA opens the external application form.
 */
function PartnershipsHero() {
  return (
    <Section className="pt-16 !pb-0 md:pt-24">
      <div className="mx-auto flex max-w-3xl flex-col items-center gap-8 text-center">
        <h1 className="text-[40px] leading-[1.1] font-semibold tracking-[-0.03em] whitespace-pre-line md:text-title-m">
          {partnershipsHero.title}
        </h1>
        <div className="mt-6 flex flex-col items-center gap-3 sm:flex-row">
          <a
            href={partnershipsHero.cta.href}
            target="_blank"
            rel="noreferrer"
            className={cn(PILL, 'bg-red-500 text-white hover:bg-red-700')}>
            <span>{partnershipsHero.cta.label}</span>
          </a>
        </div>
      </div>
    </Section>
  );
}

/**
 * Video strip — a row of decorative portrait marketing clips sitting between the
 * hero and the "Join the movement" section. Autoplay-muted-loop-playsInline is
 * required for inline autoplay across browsers. 2 columns on mobile, 5 on
 * desktop. Clips are purely decorative, so they're hidden from the a11y tree.
 */
function PartnershipsVideos() {
  return (
    <Section className="!pt-40 md:!pt-[176px]">
      <div className="grid grid-cols-2 items-start gap-4 sm:grid-cols-3 md:grid-cols-5">
        {partnershipsVideos.map((video, i) => (
          <div
            key={video.src}
            // `box-content` + `pb-10` makes each cell 40px taller than its pure
            // 640/920 aspect box at every breakpoint (aspect sizes the content
            // box, the 40px padding-bottom is added on top). The 2nd and 4th
            // clips are nudged down 64px on md+ for a staggered look; the offset
            // is gated to desktop so the 2-/3-col mobile layouts stay flush.
            className={cn(
              'relative box-content aspect-[640/920] w-full pb-10',
              (i === 1 || i === 3) && 'md:translate-y-16',
            )}>
            <video
              src={video.src}
              autoPlay
              loop
              muted
              playsInline
              preload="metadata"
              aria-hidden="true"
              tabIndex={-1}
              className="absolute inset-0 h-full w-full rounded-[24px] bg-grey-50 object-cover"
            />
          </div>
        ))}
      </div>
    </Section>
  );
}

/**
 * Partnership types — the "Join the movement. Make an impact." section: a 3-card
 * grid (icon chip + title + description + "Apply as …" link), one per partner
 * type. Uses the shared 9-col Grid and the design's icon-chip treatment.
 */
function PartnershipTypes() {
  return (
    <Section className="mt-[120px]">
      <div id="partnership-types" className="scroll-mt-24">
        <SectionHeading title={partnershipTypes.heading} />
      </div>
      <Grid className="mt-10 gap-y-10">
        {partnershipTypes.items.map((type) => (
          <article key={type.title} className="col-span-9 flex flex-col gap-6 md:col-span-3">
            <span className="flex size-12 shrink-0 items-center justify-center rounded-full bg-grey-50 text-ink">
              <Icon name={type.icon} className="size-6" />
            </span>
            <div className="mt-2 flex flex-col gap-5">
              <h3 className="text-h5 font-semibold text-ink">{type.title}</h3>
              <p className="text-body text-grey-900">{type.description}</p>
            </div>
            <a
              href={type.cta.href}
              target="_blank"
              rel="noreferrer"
              className="mt-auto inline-flex text-body-m font-medium text-red-500 hover:text-red-700">
              {type.cta.label}
            </a>
          </article>
        ))}
      </Grid>
    </Section>
  );
}

/**
 * "What can I promote?" — two-column layout on the 9-col grid: the heading,
 * paragraph and downloadable resource links on the left; a list of promotable
 * products (icon chip + title + description) on the right. Mirrors the
 * FeatureBlock text/icon-row pattern without needing an image asset.
 */
function PartnershipsPromote() {
  return (
    <Section className="mt-20">
      <Grid className="items-start gap-y-12">
        <div className="col-span-9 lg:col-span-4">
          <div className="border-b border-black-6 pb-6">
            <h2 className="text-h4 font-semibold tracking-[-0.02em] md:text-h3">
              {partnershipsPromote.title}
            </h2>
          </div>
          <p className="mt-8 text-body text-grey-900">{partnershipsPromote.body}</p>
          <div className="mt-8 flex flex-col gap-4">
            {partnershipsPromote.resources.map((resource) => (
              <a
                key={resource.label}
                href={resource.href}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 text-body font-medium text-ink underline underline-offset-4 hover:text-grey-700">
                <ArticleIcon />
                {resource.label}
              </a>
            ))}
          </div>
        </div>

        <ul className="col-span-9 lg:col-span-4 lg:col-start-6">
          {partnershipsPromote.items.map((item, i) => (
            <li key={item.title} className="flex gap-5">
              <span className="mt-8 flex size-12 shrink-0 items-center justify-center rounded-full bg-grey-50 text-ink">
                <Icon name={item.icon} className="size-6" />
              </span>
              <div className={cn('flex-1', i > 0 && 'border-t border-black-6')}>
                <div className="flex flex-col gap-5 py-8">
                  <h3 className="text-body font-semibold">{item.title}</h3>
                  <p className="text-body text-grey-900">{item.description}</p>
                </div>
              </div>
            </li>
          ))}
        </ul>
      </Grid>
    </Section>
  );
}

function PartnershipsPage() {
  return (
    <PageShell>
      <PartnershipsHero />
      <PartnershipsVideos />
      <PartnershipTypes />
      <PartnershipsPromote />
      <FaqAccordion content={partnershipsFaq} gridAligned />
      <CtaBanner />
    </PageShell>
  );
}
